import React, { useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTypes, Button } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useUpdateSelectedCustomer } from '@/generated/customer';
import { AccountId } from '@/generated/data.schemas';
import { useGetTranslations } from '@/generated/translations';
import { useNotificationStore } from '@/store/notificationStore';
import styles from './SwitchCustomer.module.scss';

interface SwitchCustomerProps {
	accountId: AccountId;
	handleNavigation: () => void;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SwitchCustomer: React.FunctionComponent<SwitchCustomerProps> = ({
	accountId,
	handleNavigation,
	setIsOpen,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const queryClient = useQueryClient();
	const addNotification = useNotificationStore((state) => state.addNotification);

	const updateSelectedCustomer = useUpdateSelectedCustomer({
		mutation: {
			onSuccess: async () => {
				await queryClient.invalidateQueries().then();
				handleNavigation();
			},
			onError: async (axiosError) => {
				const error = axiosError.response?.data;
				if (!error) {
					console.error('Unknown error', axiosError);
					addNotification({
						children: translations?.data.applications.switchCustomer.failed,
						type: AlertTypes.DANGER,
					});
				} else {
					const { response } = axiosError;
					if (response?.status === 409) {
						handleNavigation();
					} else {
						addNotification({
							children: translations?.data.applications.switchCustomer.failed,
							type: AlertTypes.DANGER,
						});
					}
				}
			},
		},
	});

	const handleSwitchCustomer = () => {
		const body = { segmentationId, data: { accountId } };

		updateSelectedCustomer.mutate(body);
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.headline}>{translations?.data.applications.switchCustomer.headline}</div>
			<div className={styles.teaser}>{translations?.data.applications.switchCustomer.teaser}</div>
			<div className={styles.buttonWrap}>
				<Button variant={'ghost'} isFullWidth onClick={() => setIsOpen(false)}>
					{translations?.data.shared.cancel}
				</Button>

				<Button onClick={handleSwitchCustomer}>
					{translations?.data.applications.switchCustomer.yesContinueBtn}
				</Button>
			</div>
		</div>
	);
};
